import {
  Dialog, DialogContent,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import {
  ChevronLeft, ChevronRight, X, Copy, Trash2, Scissors,
  ImageIcon, Video, Eye,
} from 'lucide-react'
import type { MediaImage } from '@/types'
import { AiLogDetail } from '@/components/AiLogDetail'
import { useState } from 'react'

function formatTime(seconds: number) {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

interface MediaPreviewProps {
  media: MediaImage | null
  allMedia: MediaImage[]
  onClose: () => void
  onNavigate: (media: MediaImage) => void
  onDelete: (imageId: string) => void
  onCut: (media: MediaImage) => void
}

export function MediaPreview({ media, allMedia, onClose, onNavigate, onDelete, onCut }: MediaPreviewProps) {
  const [showAiLog, setShowAiLog] = useState(false)
  if (!media) return null

  const currentIndex = allMedia.findIndex(m => m.id === media.id)
  const hasPrev = currentIndex > 0
  const hasNext = currentIndex < allMedia.length - 1

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(media.url)
    toast.success('URL copiada!')
  }

  const handleDelete = () => {
    if (!confirm('Remover este arquivo?')) return
    onDelete(media.id)
    onClose()
  }

  const handlePrev = () => {
    if (hasPrev) onNavigate(allMedia[currentIndex - 1])
  }

  const handleNext = () => {
    if (hasNext) onNavigate(allMedia[currentIndex + 1])
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') handlePrev()
    if (e.key === 'ArrowRight') handleNext()
  }

  return (
    <Dialog open={!!media} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent
        className="flex h-[90vh] max-w-5xl flex-col gap-0 p-0 overflow-hidden"
        showCloseButton={false}
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <div className="flex items-center gap-2 min-w-0">
            {media.mediaType === 'video' ? (
              <Video className="h-4 w-4 shrink-0 text-muted-foreground" />
            ) : (
              <ImageIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
            <span className="truncate text-sm font-medium">{media.filename}</span>
            {media.clip && (
              <Badge className="shrink-0 bg-primary/10 text-primary border-primary/20 text-[10px] px-1.5">
                Parte {media.clip.part}/{media.clip.total}
              </Badge>
            )}
            {media.publishedTag && media.publishedTag.length > 0 && (
              <Badge className="shrink-0 border-none bg-green-600/90 text-[10px] text-white px-1.5">
                Publicado
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCopyUrl} title="Copiar URL">
              <Copy className="h-3.5 w-3.5" />
            </Button>
            {media.mediaType === 'video' && !media.clip && (
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { onClose(); setTimeout(() => onCut(media), 150) }} title="Cortar video">
                <Scissors className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={handleDelete} title="Remover">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="relative flex flex-1 items-center justify-center bg-black/95 overflow-hidden">
          {/* Navigation arrows */}
          {hasPrev && (
            <button
              className="absolute left-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
              onClick={handlePrev}
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}
          {hasNext && (
            <button
              className="absolute right-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
              onClick={handleNext}
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          )}

          {/* Media */}
          {media.mediaType === 'video' ? (
            <video
              key={media.id}
              src={media.url}
              controls
              autoPlay
              className="max-h-full max-w-full"
            />
          ) : (
            <img
              key={media.id}
              src={media.url}
              alt={media.filename}
              className="max-h-full max-w-full object-contain"
            />
          )}
        </div>

        {/* AI Analysis Log */}
        {media.clip?.aiLog && media.clip.aiLog.segments.length > 0 && (
          <div className="border-t border-border">
            <button
              className="flex w-full items-center gap-2 px-4 py-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setShowAiLog(!showAiLog)}
            >
              <Eye className="h-3.5 w-3.5" />
              Analise IA ({media.clip.aiLog.clipHits} deteccoes)
              <span className="ml-auto text-[10px]">{showAiLog ? 'Fechar' : 'Abrir'}</span>
            </button>
            {showAiLog && (
              <div className="px-4 pb-3 max-h-[30vh] overflow-y-auto">
                <AiLogDetail aiLog={media.clip.aiLog} clipFilename={media.filename} />
              </div>
            )}
          </div>
        )}

        {/* Footer info */}
        <div className="flex items-center justify-between border-t border-border px-4 py-2 text-xs text-muted-foreground">
          <span>{currentIndex + 1} de {allMedia.length}</span>
          {media.clip && (
            <span>{formatTime(media.clip.startTime)} - {formatTime(media.clip.endTime)} ({media.clip.duration}s)</span>
          )}
          <span>Setas &larr;&rarr; para navegar</span>
        </div>
      </DialogContent>
    </Dialog>
  )
}
